"use client";

import { useAuth } from "@/contexts/AuthContext";

/**
 * MaintenanceScreen — shown to all users except the admin
 * when NEXT_PUBLIC_MAINTENANCE_MODE=true.
 *
 * Terminal aesthetic — on-brand for "Bash". Animated typing effect
 * with a blinking cursor showing maintenance log lines.
 */
export function MaintenanceScreen() {
  const { signOut } = useAuth();

  return (
    <>
      <style>{`
        .maint {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #09090b;
          color: #fafafa;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          -webkit-font-smoothing: antialiased;
          position: relative;
          overflow: hidden;
        }

        /* Noise texture */
        .maint::after {
          content: '';
          position: absolute;
          inset: 0;
          opacity: 0.03;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
          pointer-events: none;
        }

        .maint__container {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 520px;
          padding: 0 24px;
        }

        .maint__brand {
          font-family: var(--font-fauna, Georgia, serif);
          font-size: 26px;
          font-weight: 400;
          line-height: 1;
          color: #fafafa;
          text-align: center;
          margin-bottom: 40px;
          letter-spacing: 0.02em;
        }

        /* Terminal window */
        .maint__terminal {
          background: #0c0c0f;
          border: 1px solid rgba(255, 255, 255, 0.06);
          border-radius: 12px;
          overflow: hidden;
        }

        .maint__terminal-bar {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 12px 16px;
          background: rgba(255, 255, 255, 0.03);
          border-bottom: 1px solid rgba(255, 255, 255, 0.04);
        }

        .maint__terminal-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.08);
        }

        .maint__terminal-dot--amber {
          background: #f59e0b;
          box-shadow: 0 0 8px rgba(245, 158, 11, 0.3);
          animation: maint-glow 2s ease-in-out infinite;
        }

        @keyframes maint-glow {
          0%, 100% { box-shadow: 0 0 8px rgba(245, 158, 11, 0.3); }
          50% { box-shadow: 0 0 14px rgba(245, 158, 11, 0.5); }
        }

        .maint__terminal-title {
          margin-left: 8px;
          font-family: var(--font-mono, ui-monospace, monospace);
          font-size: 11px;
          color: rgba(255, 255, 255, 0.25);
          letter-spacing: 0.02em;
        }

        .maint__terminal-body {
          padding: 20px 16px;
          font-family: var(--font-mono, ui-monospace, monospace);
          font-size: 13px;
          line-height: 2;
        }

        .maint__line {
          opacity: 0;
          animation: maint-type 0.3s ease forwards;
        }

        .maint__line:nth-child(1) { animation-delay: 0.2s; }
        .maint__line:nth-child(2) { animation-delay: 0.8s; }
        .maint__line:nth-child(3) { animation-delay: 1.4s; }
        .maint__line:nth-child(4) { animation-delay: 2.0s; }
        .maint__line:nth-child(5) { animation-delay: 2.6s; }

        @keyframes maint-type {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .maint__prompt {
          color: #f59e0b;
          user-select: none;
        }

        .maint__cmd {
          color: rgba(255, 255, 255, 0.7);
        }

        .maint__comment {
          color: rgba(255, 255, 255, 0.2);
          font-style: italic;
        }

        .maint__ok {
          color: #22c55e;
        }

        .maint__cursor {
          display: inline-block;
          width: 8px;
          height: 16px;
          background: #f59e0b;
          vertical-align: text-bottom;
          margin-left: 2px;
          animation: maint-blink 1s step-end infinite;
        }

        @keyframes maint-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }

        /* Message below terminal */
        .maint__message {
          text-align: center;
          margin-top: 32px;
        }

        .maint__title {
          font-size: 16px;
          font-weight: 600;
          color: #fafafa;
          margin: 0 0 8px;
          letter-spacing: -0.01em;
        }

        .maint__desc {
          font-size: 13px;
          line-height: 1.6;
          color: rgba(255, 255, 255, 0.4);
          margin: 0;
        }

        .maint__signout {
          display: block;
          margin: 28px auto 0;
          font-family: 'Inter', system-ui, sans-serif;
          font-size: 11px;
          color: rgba(255, 255, 255, 0.15);
          background: none;
          border: none;
          cursor: pointer;
          letter-spacing: 0.03em;
          transition: color 0.2s;
        }

        .maint__signout:hover {
          color: rgba(255, 255, 255, 0.4);
        }
      `}</style>

      <div className="maint">
        <div className="maint__container">
          <div className="maint__brand">Vestra</div>

          <div className="maint__terminal">
            <div className="maint__terminal-bar">
              <span className="maint__terminal-dot" />
              <span className="maint__terminal-dot maint__terminal-dot--amber" />
              <span className="maint__terminal-dot" />
              <span className="maint__terminal-title">
                bash &mdash; maintenance
              </span>
            </div>
            <div className="maint__terminal-body">
              <div className="maint__line">
                <span className="maint__prompt">$ </span>
                <span className="maint__cmd">bash deploy --upgrade</span>
              </div>
              <div className="maint__line">
                <span className="maint__comment">
                  # optimizing agent runtime...
                </span>
              </div>
              <div className="maint__line">
                <span className="maint__ok">&#10003;</span>{" "}
                <span className="maint__cmd">tasks backed up</span>
              </div>
              <div className="maint__line">
                <span className="maint__ok">&#10003;</span>{" "}
                <span className="maint__cmd">sessions preserved</span>
              </div>
              <div className="maint__line">
                <span className="maint__prompt">$ </span>
                <span className="maint__cursor" />
              </div>
            </div>
          </div>

          <div className="maint__message">
            <h1 className="maint__title">Back in a few minutes</h1>
            <p className="maint__desc">
              Your tasks and data are safe. We&apos;re tuning things up.
            </p>
          </div>

          <button onClick={signOut} className="maint__signout">
            Sign out
          </button>
        </div>
      </div>
    </>
  );
}
